import { useEffect, useRef, useState } from 'react';
import { marketApi } from '@/lib/api';

interface SaleItem {
  key: number;
  title: string;
  image: string;
  price: number;
}

const INITIAL_COUNT = 20_312;
const ITEM_W = 206;       // 200px карточка + 3px margin с каждой стороны
const COUNTER_GAP = 12;  // отступ между правым краем блока счётчика и первой карточкой
const TRANSITION_MS = 360;
const PREFILL = 16;
const MIN_DELAY = 1800;
const MAX_DELAY = 4400;
let uid = 0;

function randomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function buildWeightedPool(
  pool: { title: string; image: string; price: number }[],
): typeof pool {
  const out: typeof pool = [];
  for (const item of pool) {
    const w = item.price < 2_000 ? 5 : item.price < 15_000 ? 2 : 1;
    for (let i = 0; i < w; i++) out.push(item);
  }
  return out;
}

function pickRandom<T>(arr: T[]): T {
  return arr[randomInt(0, arr.length - 1)];
}

export function LiveTicker() {
  const [items, setItems] = useState<SaleItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [sliding, setSliding] = useState(false);
  const [rightOverlay, setRightOverlay] = useState(0);
  // реальная ширина блока счётчика — измеряется ResizeObserver
  const [counterW, setCounterW] = useState(160);
  const [totalCount, setTotalCount] = useState(() => {
    try {
      const saved = localStorage.getItem('xpanda_ticker_count');
      return saved ? Math.max(INITIAL_COUNT, Number(saved)) : INITIAL_COUNT;
    } catch { return INITIAL_COUNT; }
  });

  const outerRef = useRef<HTMLDivElement>(null);
  const counterRef = useRef<HTMLDivElement>(null);
  const weightedRef = useRef<{ title: string; image: string; price: number }[]>([]);
  const lockRef = useRef(false);
  const nextTickRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    marketApi.getItems({ limit: 96, sort: 'popular' })
      .then((data: any) => {
        const raw: { name?: string; image?: string; price_rub_display?: number }[] =
          data?.items ?? (Array.isArray(data) ? data : []);

        const clean = raw
          .filter(i => i.image && (i.price_rub_display || 0) > 0)
          .map(i => ({
            title: (i.name || '').replace(/\s+/g, ' ').trim(),
            image: i.image as string,
            price: Math.round(i.price_rub_display || 0),
          }))
          .filter(i => i.title && i.price > 0);

        if (clean.length === 0) return;

        weightedRef.current = buildWeightedPool(clean);
        const initial: SaleItem[] = Array.from({ length: PREFILL }, () => ({
          key: uid++,
          ...pickRandom(weightedRef.current),
        }));
        setItems(initial);
      })
      .catch(() => {});
  }, []);

  // Измеряем ширину блока счётчика и вычисляем правый оверлей.
  // Ширина счётчика зависит от контента и может меняться при ресайзе.
  useEffect(() => {
    const outer = outerRef.current;
    const counter = counterRef.current;
    if (!outer || !counter) return;

    const measure = () => {
      const cw = counter.offsetWidth;
      setCounterW(cw);
      const scrollW = outer.offsetWidth - cw - COUNTER_GAP;
      const fullCards = Math.floor(scrollW / ITEM_W);
      setRightOverlay(Math.max(0, scrollW - fullCards * ITEM_W));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(outer);
    ro.observe(counter);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (items.length === 0 || startedRef.current) return;
    startedRef.current = true;

    const scheduleTick = () => {
      nextTickRef.current = setTimeout(tick, randomInt(MIN_DELAY, MAX_DELAY));
    };

    const tick = () => {
      if (lockRef.current || weightedRef.current.length === 0) {
        scheduleTick();
        return;
      }
      lockRef.current = true;

      const newItem: SaleItem = { key: uid++, ...pickRandom(weightedRef.current) };
      setItems(prev => [...prev, newItem]);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSliding(true);
          setOffset(-ITEM_W);
        });
      });

      setTimeout(() => {
        setSliding(false);
        setOffset(0);
        setItems(prev => prev.slice(1));
        setTotalCount(c => {
          const next = c + 1;
          try { localStorage.setItem('xpanda_ticker_count', String(next)); } catch {}
          return next;
        });
        lockRef.current = false;
        scheduleTick();
      }, TRANSITION_MS + 80);
    };

    scheduleTick();
    return () => { if (nextTickRef.current) clearTimeout(nextTickRef.current); };
  }, [items.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (items.length === 0) return null;

  return (
    <div
      ref={outerRef}
      className="w-full bg-[#0a0e16] border border-[#1a2332] rounded-xl mb-3 overflow-hidden relative"
      style={{ minHeight: '4.5rem' }}
    >

      {/* Карточки — z-0, начинаются за правым краем блока счётчика */}
      <div
        className="absolute inset-0 flex items-center"
        style={{
          transform: `translateX(${counterW + COUNTER_GAP + offset}px)`,
          transition: sliding
            ? `transform ${TRANSITION_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
            : 'none',
        }}
      >
        {items.map(item => (
          <div
            key={item.key}
            className="flex-shrink-0 flex items-center gap-2 mx-1.5 px-3 rounded-lg bg-[#0d1117] border border-[#1a2332]"
            style={{ width: '200px', height: '64px' }}
          >
            <img
              src={item.image}
              alt={item.title}
              className="w-10 h-10 object-contain flex-shrink-0"
              loading="lazy"
            />
            <div className="min-w-0">
              <p className="text-[#c9d1d9] text-xs font-medium leading-snug line-clamp-2">{item.title}</p>
              <p className="text-[#ff6b00] text-sm font-semibold mt-0.5 whitespace-nowrap">
                {item.price.toLocaleString('ru-RU')} ₽
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Блок счётчика — z-10, сплошной фон перекрывает уходящие карточки слева.
          Без фиксированной ширины — растягивается по контенту. */}
      <div
        ref={counterRef}
        className="absolute left-0 top-0 bottom-0 z-10 flex flex-col justify-center bg-[#0a0e16] border-r border-[#1a2332] px-4 sm:px-5 py-2"
      >
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="relative flex h-2 w-2 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
          <span className="text-[#8b949e] text-[10px] font-medium uppercase tracking-wider whitespace-nowrap">
            Последние сделки
          </span>
        </div>
        <p className="text-[#6e7681] text-[10px] leading-tight whitespace-nowrap">Всего операций</p>
        <p className="text-[#ff6b00] font-bold text-sm leading-tight">{totalCount.toLocaleString('ru-RU')}</p>
      </div>

      {/* Правый оверлей — перекрывает неполную карточку у правого края */}
      {rightOverlay > 0 && (
        <div
          className="absolute top-0 bottom-0 right-0 z-10 bg-[#0a0e16]"
          style={{ width: rightOverlay }}
        />
      )}

    </div>
  );
}
